import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { DateTimePicker } from '@/components/DateTimePicker';
import { ConfirmModal } from '@/components/ConfirmModal';
import moment from 'moment';
import { currentUserHasPermission } from '@/utils/legacy-utils';
import { AccessAuthorities } from '@/models';
import { App } from '@/services/app-compat';

const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const getInsecureUUID = _wg('getInsecureUUID');
const scrollToLastMobileCard = _wg('scrollToLastMobileCard');

interface FieldInfo {
  label: string;
  field: string;
  type: string;
}

interface EditingField {
  uuid: string;
  field: string;
}

interface DeleteConfirmState {
  open: boolean;
  uuid: string | null;
  name: string;
}

interface CallCardPersonGridProps {
  people: any[];
  title?: string;
  isReadOnly?: boolean;
  onChange?: (people: any[]) => void;
  onLinkPerson?: (uuid: string) => void;
  onShowLinkedPerson?: (npsdPerson: any) => void;
  onCreateNpsdPerson?: (uuid: string, person: any) => void;
}

const FIELDS: FieldInfo[] = [
  { label: 'Last Name', field: 'lastName', type: 'text' },
  { label: 'First Name', field: 'firstName', type: 'text' },
  { label: 'D.O.B', field: 'dob', type: 'date' },
  { label: 'License #', field: 'license', type: 'text' },
];

export function CallCardPersonGrid({
  people,
  title = 'Person Details',
  isReadOnly = false,
  onChange,
  onLinkPerson,
  onShowLinkedPerson,
  onCreateNpsdPerson,
}: CallCardPersonGridProps) {
  const { theme } = useTheme();

  const [editingField, setEditingField] = useState<EditingField | null>(null);
  const [editValue, setEditValue] = useState<any>('');
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [datePickerOpenDesktop, setDatePickerOpenDesktop] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTabbing, setIsTabbing] = useState(false);
  const [isJustStartingEdit, setIsJustStartingEdit] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState>({
    open: false,
    uuid: null,
    name: '',
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<any>(null);
  const editingFieldRef = useRef(editingField);
  const editValueRef = useRef(editValue);
  const isJustStartingEditRef = useRef(isJustStartingEdit);
  const isTabRef = useRef(isTabbing);
  const peopleRef = useRef(people);
  const pendingEditActionRef = useRef<string | null>(null);
  const shouldScrollToLastRef = useRef(false);

  const canCreateCitation = useMemo(
    () => currentUserHasPermission(AccessAuthorities.ROLE_CITATION_CREATE),
    []
  );

  // Keep refs in sync
  useEffect(() => { editingFieldRef.current = editingField; }, [editingField]);
  useEffect(() => { editValueRef.current = editValue; }, [editValue]);
  useEffect(() => { isJustStartingEditRef.current = isJustStartingEdit; }, [isJustStartingEdit]);
  useEffect(() => { isTabRef.current = isTabbing; }, [isTabbing]);
  useEffect(() => { peopleRef.current = people; }, [people]);

  // Mobile check (debounced to prevent re-render thrashing)
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const checkIfMobile = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setIsMobile(window.innerWidth <= 1024), 150);
    };
    setIsMobile(window.innerWidth <= 1024);
    window.addEventListener('resize', checkIfMobile);
    return () => {
      window.removeEventListener('resize', checkIfMobile);
      if (timer) clearTimeout(timer);
    };
  }, []);

  // After people array changes, scroll to last card if requested
  useEffect(() => {
    if (shouldScrollToLastRef.current) {
      shouldScrollToLastRef.current = false;
      if (containerRef.current) scrollToLastMobileCard(containerRef.current);
    }
  }, [people]);

  // Date picker repositioning
  useEffect(() => {
    if (datePickerOpenDesktop || datePickerOpen) {
      repositionDatePickerPopup();
    }
  }, [datePickerOpenDesktop, datePickerOpen]);

  const emitReplace = useCallback((newArr: any[]) => {
    onChange?.(newArr);
  }, [onChange]);

  const formatDate = useCallback((date: any): string => {
    if (!date) return '';
    const m = moment.utc(date);
    if (!m.isValid()) return '';
    return m.format('DD MMM YYYY');
  }, []);

  const repositionDatePickerPopup = useCallback(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const popup = document.querySelector('.datetime-picker-portal') as HTMLElement | null;
        const control = document.querySelector('.edit-datetime .datetime-picker-control') as HTMLElement | null
          || document.querySelector('.edit-datetime .datetime-picker-wrapper') as HTMLElement | null
          || document.querySelector('.edit-datetime') as HTMLElement | null;
        if (popup && control) {
          const controlRect = control.getBoundingClientRect();
          if (controlRect.top === 0 && controlRect.bottom === 0) return;
          const popupHeight = popup.offsetHeight || 300;
          const viewportHeight = window.innerHeight;
          const spaceBelow = viewportHeight - controlRect.bottom;
          const spaceAbove = controlRect.top;
          popup.style.position = 'fixed';
          popup.style.zIndex = '9999';
          if (spaceBelow >= popupHeight || spaceBelow >= spaceAbove) {
            popup.style.top = (controlRect.bottom + 5) + 'px';
            popup.style.bottom = '';
          } else {
            popup.style.top = '';
            popup.style.bottom = (viewportHeight - controlRect.top + 5) + 'px';
          }
        }
      });
    });
  }, []);

  const isEditing = useCallback((rowUuid: string, field: string): boolean => {
    return editingField?.uuid === rowUuid && editingField?.field === field;
  }, [editingField]);

  const focusInput = useCallback(() => {
    requestAnimationFrame(() => {
      if (!containerRef.current) return;
      const editContainer = containerRef.current.querySelector('.edit-container, .edit-field-mobile');
      if (editContainer) {
        const input = editContainer.querySelector('.edit-input:not([type="hidden"])') as HTMLInputElement;
        if (input) {
          if (isMobile) {
            setTimeout(() => {
              input.focus();
              input.click();
            }, 150);
          } else {
            input.focus();
            if (input.type === 'text') {
              input.select();
            }
          }
        }
      }
    });
  }, [isMobile]);

  // After editingField renders, perform any pending action (focus input or open date picker)
  useEffect(() => {
    if (!editingField || !pendingEditActionRef.current) return;
    const action = pendingEditActionRef.current;
    pendingEditActionRef.current = null;

    if (action === 'focusInput') {
      focusInput();
    } else if (action === 'datePickerDesktop') {
      setDatePickerOpenDesktop(true);
    } else if (action === 'datePickerMobile') {
      setDatePickerOpen(true);
      setIsJustStartingEdit(false);
    }
  }, [editingField, focusInput]);

  const saveEditDirect = useCallback(() => {
    const ef = editingFieldRef.current;
    if (!ef) return;
    if (isJustStartingEditRef.current) return;
    const { uuid, field } = ef;
    const val = editValueRef.current;
    const newArr = peopleRef.current.map((p: any) =>
      p.uuid === uuid ? { ...p, [field]: val } : p
    );
    emitReplace(newArr);
  }, [emitReplace]);

  const closeEditMode = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setEditingField(null);
    setEditValue('');
    setDatePickerOpen(false);
    setDatePickerOpenDesktop(false);
  }, []);

  const getNextEditableField = useCallback((currentUuid: string, currentField: string) => {
    const editableFields = ['lastName', 'firstName', 'dob', 'license', 'notes'];
    const currentPersonIndex = peopleRef.current.findIndex((p: any) => p.uuid === currentUuid);
    const currentFieldIndex = editableFields.indexOf(currentField);
    const person = peopleRef.current[currentPersonIndex];
    const allowedFields = person?.npsdPerson ? ['notes'] : editableFields;
    for (let i = currentFieldIndex + 1; i < editableFields.length; i++) {
      if (allowedFields.indexOf(editableFields[i]) >= 0) {
        return { uuid: currentUuid, field: editableFields[i] };
      }
    }
    if (currentPersonIndex < peopleRef.current.length - 1) {
      const nextPerson = peopleRef.current[currentPersonIndex + 1];
      const nextAllowedFields = nextPerson?.npsdPerson ? ['notes'] : editableFields;
      return { uuid: nextPerson.uuid, field: nextAllowedFields[0] };
    }
    return null;
  }, []);

  const getPreviousEditableField = useCallback((currentUuid: string, currentField: string) => {
    const editableFields = ['lastName', 'firstName', 'dob', 'license', 'notes'];
    const currentPersonIndex = peopleRef.current.findIndex((p: any) => p.uuid === currentUuid);
    const currentFieldIndex = editableFields.indexOf(currentField);
    const person = peopleRef.current[currentPersonIndex];
    const allowedFields = person?.npsdPerson ? ['notes'] : editableFields;
    for (let i = currentFieldIndex - 1; i >= 0; i--) {
      if (allowedFields.indexOf(editableFields[i]) >= 0) {
        return { uuid: currentUuid, field: editableFields[i] };
      }
    }
    if (currentPersonIndex > 0) {
      const prevPerson = peopleRef.current[currentPersonIndex - 1];
      const prevAllowedFields = prevPerson?.npsdPerson ? ['notes'] : editableFields;
      return { uuid: prevPerson.uuid, field: prevAllowedFields[prevAllowedFields.length - 1] };
    }
    return null;
  }, []);

  const startEdit = useCallback((rowUuid: string, field: string, currentValue: any) => {
    if (isReadOnly) return;
    const person = peopleRef.current.find((p: any) => p.uuid === rowUuid);
    if (person?.npsdPerson && field !== 'notes') return;

    if (editingFieldRef.current && (editingFieldRef.current.uuid !== rowUuid || editingFieldRef.current.field !== field)) {
      saveEditDirect();
      closeEditMode();
    }

    setEditingField({ uuid: rowUuid, field });
    if (field === 'dob') {
      setEditValue(currentValue !== null ? currentValue : new Date(2000, 0, 1));
      pendingEditActionRef.current = 'datePickerDesktop';
    } else {
      setEditValue(currentValue || '');
      pendingEditActionRef.current = 'focusInput';
    }
  }, [isReadOnly, saveEditDirect, closeEditMode]);

  const startEditMobile = useCallback((rowUuid: string, field: string, currentValue: any) => {
    if (isReadOnly) return;
    const person = peopleRef.current.find((p: any) => p.uuid === rowUuid);
    if (person?.npsdPerson && field !== 'notes') return;

    if (editingFieldRef.current && (editingFieldRef.current.uuid !== rowUuid || editingFieldRef.current.field !== field)) {
      saveEditDirect();
      closeEditMode();
    }

    setIsJustStartingEdit(true);
    setEditingField({ uuid: rowUuid, field });

    if (field === 'dob') {
      setEditValue(currentValue ?? new Date(2000, 0, 1));
      pendingEditActionRef.current = 'datePickerMobile';
    } else {
      setEditValue(currentValue || '');
      setTimeout(() => {
        setIsJustStartingEdit(false);
      }, 200);
    }
  }, [isReadOnly, saveEditDirect, closeEditMode]);

  const handleTabKey = useCallback((event: React.KeyboardEvent) => {
    if (!editingFieldRef.current) return;
    event.preventDefault();
    event.stopPropagation();

    setIsTabbing(true);
    const currentField = { ...editingFieldRef.current };
    const isShiftPressed = event.shiftKey;

    saveEditDirect();
    closeEditMode();

    setTimeout(() => {
      const next = isShiftPressed
        ? getPreviousEditableField(currentField.uuid, currentField.field)
        : getNextEditableField(currentField.uuid, currentField.field);

      if (next) {
        const person = peopleRef.current.find((p: any) => p.uuid === next.uuid);
        if (person) {
          startEdit(next.uuid, next.field, person[next.field]);
        }
      }
      setIsTabbing(false);
    }, 0);
  }, [saveEditDirect, closeEditMode, getNextEditableField, getPreviousEditableField, startEdit]);

  const handleDateChange = useCallback((v: any) => {
    const newVal = (v === null || v === undefined || v === '') ? null : v;
    editValueRef.current = newVal;
    setEditValue(newVal);
  }, []);

  const clearDate = useCallback(() => {
    setEditValue(null);
    // Save with null value
    if (editingFieldRef.current) {
      const { uuid, field } = editingFieldRef.current;
      const newArr = peopleRef.current.map((p: any) =>
        p.uuid === uuid ? { ...p, [field]: null } : p
      );
      emitReplace(newArr);
    }
    closeEditMode();
  }, [emitReplace, closeEditMode]);

  const handleDatePickerClose = useCallback(() => {
    setDatePickerOpenDesktop(false);
    if (isTabRef.current) return;
    saveEditDirect();
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => {
      closeEditMode();
    }, 150);
  }, [saveEditDirect, closeEditMode]);

  const addPerson = useCallback(() => {
    if (isReadOnly) {
      if (typeof App?.showPopupNotification === 'function') {
        App.showPopupNotification('Edit mode required.', 'warning');
      }
      return;
    }
    const newPerson = {
      uuid: getInsecureUUID(),
      id: null,
      firstName: '',
      lastName: '',
      dob: null,
      license: '',
      notes: '',
      npsdPerson: null,
    };
    emitReplace([...people, newPerson]);
    shouldScrollToLastRef.current = true;
  }, [isReadOnly, people, emitReplace]);

  const openDeleteConfirm = useCallback((uuid: string) => {
    if (isReadOnly) {
      if (typeof App?.showPopupNotification === 'function') {
        App.showPopupNotification('Edit mode required.', 'warning');
      }
      return;
    }
    const row = people.find((p: any) => p.uuid === uuid);
    setDeleteConfirm({
      open: true,
      uuid,
      name: row ? `${row.firstName} ${row.lastName}` : 'this person',
    });
  }, [isReadOnly, people]);

  const cancelDelete = useCallback(() => {
    setDeleteConfirm({ open: false, uuid: null, name: '' });
  }, []);

  const confirmDelete = useCallback(() => {
    const uuid = deleteConfirm.uuid;
    if (!uuid) { cancelDelete(); return; }
    emitReplace(people.filter((p: any) => p.uuid !== uuid));
    cancelDelete();
  }, [deleteConfirm.uuid, people, emitReplace, cancelDelete]);

  const linkToPerson = useCallback((uuid: string) => {
    if (isReadOnly) {
      if (typeof App?.showPopupNotification === 'function') {
        App.showPopupNotification('Edit mode required.', 'warning');
      }
      return;
    }
    onLinkPerson?.(uuid);
  }, [isReadOnly, onLinkPerson]);

  const unlinkFromPerson = useCallback((uuid: string) => {
    if (isReadOnly) {
      if (typeof App?.showPopupNotification === 'function') {
        App.showPopupNotification('Edit mode required.', 'warning');
      }
      return;
    }
    emitReplace(people.map((p: any) => (p.uuid === uuid ? { ...p, npsdPerson: null } : p)));
  }, [isReadOnly, people, emitReplace]);

  const showLinkedPerson = useCallback((uuid: string) => {
    const person = people.find((p: any) => p.uuid === uuid);
    if (person?.npsdPerson) {
      onShowLinkedPerson?.(person.npsdPerson);
    }
  }, [people, onShowLinkedPerson]);

  const createNpsdPerson = useCallback((uuid: string) => {
    if (isReadOnly) {
      if (typeof App?.showPopupNotification === 'function') {
        App.showPopupNotification('Edit mode required.', 'warning');
      }
      return;
    }
    const person = people.find((p: any) => p.uuid === uuid);
    if (!person) return;
    if (!person.firstName || !person.lastName) {
      const missingFields: string[] = [];
      if (!person.firstName) missingFields.push('first name');
      if (!person.lastName) missingFields.push('last name');
      const message = `Person requires ${missingFields.join(', ')}.`;
      if (typeof App !== 'undefined' && typeof App.showPopupNotification === 'function') {
        App.showPopupNotification(message, 'error');
      }
      return;
    }
    onCreateNpsdPerson?.(uuid, person);
  }, [isReadOnly, people, onCreateNpsdPerson]);

  const citePerson = useCallback((uuid: string) => {
    const person = people.find((p: any) => p.uuid === uuid);
    if (!person || !person.npsdPerson) {
      if (typeof App?.showPopupNotification === 'function') {
        App.showPopupNotification('Cannot cite unlinked person.', 'error');
      }
      return;
    }
    if (typeof (window as any).createCitationFromCallCardPerson === 'function') {
      const dummyElement = document.createElement('a');
      dummyElement.setAttribute('data-npsd-person-id', person.npsdPerson);
      dummyElement.setAttribute('data-person-name', `${person.firstName} ${person.lastName}`);
      (window as any).createCitationFromCallCardPerson(dummyElement);
    }
  }, [people]);

  // Public method: forceSaveAndCloseAllEdits
  const forceSaveAndCloseAllEdits = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    if (editingFieldRef.current) {
      if (!isJustStartingEditRef.current) {
        saveEditDirect();
      }
      setEditingField(null);
      setEditValue('');
      setDatePickerOpen(false);
      setDatePickerOpenDesktop(false);
      setIsJustStartingEdit(false);
    }
  }, [saveEditDirect]);

  // Expose forceSaveAndCloseAllEdits on window
  useEffect(() => {
    (window as any).CallCardPersonGrid_forceSave = forceSaveAndCloseAllEdits;
    return () => { delete (window as any).CallCardPersonGrid_forceSave; };
  }, [forceSaveAndCloseAllEdits]);

  const datePickerPopupClass = theme || '';

  const handleSaveEdit = useCallback(() => {
    saveEditDirect();
  }, [saveEditDirect]);

  const handleCancelEdit = useCallback(() => {
    closeEditMode();
  }, [closeEditMode]);

  return (
    <div className={`people-assigned ${theme}`} ref={containerRef}>
      <div className="header">
        <h1>{title}</h1>
        <button
          className="btn-add"
          onClick={addPerson}
          disabled={isReadOnly}
          title={isReadOnly ? 'Edit mode required' : 'Add person'}
        >
          <i className="fas fa-plus"></i> PERSON
        </button>
      </div>

      {/* Desktop Table */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th style={{ width: '15%' }}>Last Name</th>
              <th style={{ width: '15%' }}>First Name</th>
              <th style={{ width: '12%' }}>D.O.B</th>
              <th style={{ width: '15%' }}>License #</th>
              <th style={{ width: '25%' }}>Notes</th>
              <th style={{ width: '10%' }}>Link</th>
              <th style={{ width: '8%' }}>Actions</th>
            </tr>
          </thead>
          {people.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                  No people assigned
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {people.map((person: any) => (
                <tr key={person.uuid}>
                  {/* Last Name */}
                  <td>
                    {isEditing(person.uuid, 'lastName') ? (
                      <div className="edit-container">
                        <input
                          className="edit-input"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={handleSaveEdit}
                          onKeyUp={(e) => { if (e.key === 'Enter') handleSaveEdit(); if (e.key === 'Escape') handleCancelEdit(); }}
                          onKeyDown={(e) => { if (e.key === 'Tab') handleTabKey(e); }}
                        />
                      </div>
                    ) : (
                      <div
                        className={`${!isReadOnly ? 'editable' : ''} ${person.npsdPerson ? 'linked' : ''}`}
                        onClick={(e) => { e.stopPropagation(); person.npsdPerson ? showLinkedPerson(person.uuid) : startEdit(person.uuid, 'lastName', person.lastName); }}
                      >
                        {person.lastName || '\u2014'}
                      </div>
                    )}
                  </td>

                  {/* First Name */}
                  <td>
                    {isEditing(person.uuid, 'firstName') ? (
                      <div className="edit-container">
                        <input
                          className="edit-input"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={handleSaveEdit}
                          onKeyUp={(e) => { if (e.key === 'Enter') handleSaveEdit(); if (e.key === 'Escape') handleCancelEdit(); }}
                          onKeyDown={(e) => { if (e.key === 'Tab') handleTabKey(e); }}
                        />
                      </div>
                    ) : (
                      <div
                        className={`${!isReadOnly && !person.npsdPerson ? 'editable' : ''}`}
                        onClick={(e) => { e.stopPropagation(); if (!person.npsdPerson) startEdit(person.uuid, 'firstName', person.firstName); }}
                      >
                        {person.firstName || '\u2014'}
                      </div>
                    )}
                  </td>

                  {/* DOB */}
                  <td>
                    {isEditing(person.uuid, 'dob') ? (
                      <div className="edit-datetime">
                        <div className="datetime-edit-controls">
                          <DateTimePicker
                            value={editValue}
                            type="date"
                            clearable={false}
                            appendToBody={true}
                            popupClass={datePickerPopupClass}
                            open={datePickerOpenDesktop}
                            onOpenChange={setDatePickerOpenDesktop}
                            onChange={handleDateChange}
                            onConfirm={handleSaveEdit}
                            onClear={clearDate}
                            onClose={handleDatePickerClose}
                            onKeyDown={(e) => { if (e.key === 'Tab') handleTabKey(e as any); }}
                          />
                          <button className="btn-clear-date" onClick={clearDate} title="Clear date">
                            <i className="fas fa-times-circle"></i>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        className={`${!isReadOnly && !person.npsdPerson ? 'editable' : ''}`}
                        onClick={(e) => { e.stopPropagation(); if (!person.npsdPerson) startEdit(person.uuid, 'dob', person.dob); }}
                      >
                        {person.dob ? formatDate(person.dob) : '\u2014'}
                      </div>
                    )}
                  </td>

                  {/* License */}
                  <td>
                    {isEditing(person.uuid, 'license') ? (
                      <div className="edit-container">
                        <input
                          className="edit-input"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={handleSaveEdit}
                          onKeyUp={(e) => { if (e.key === 'Enter') handleSaveEdit(); if (e.key === 'Escape') handleCancelEdit(); }}
                          onKeyDown={(e) => { if (e.key === 'Tab') handleTabKey(e); }}
                        />
                      </div>
                    ) : (
                      <div
                        className={`${!isReadOnly && !person.npsdPerson ? 'editable' : ''}`}
                        onClick={(e) => { e.stopPropagation(); if (!person.npsdPerson) startEdit(person.uuid, 'license', person.license); }}
                      >
                        {person.license || '\u2014'}
                      </div>
                    )}
                  </td>

                  {/* Notes */}
                  <td>
                    {isEditing(person.uuid, 'notes') ? (
                      <div className="edit-container">
                        <input
                          className="edit-input"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={handleSaveEdit}
                          onKeyUp={(e) => { if (e.key === 'Enter') handleSaveEdit(); if (e.key === 'Escape') handleCancelEdit(); }}
                          onKeyDown={(e) => { if (e.key === 'Tab') handleTabKey(e); }}
                        />
                      </div>
                    ) : (
                      <div
                        className={`${!isReadOnly && !person.npsdPerson ? 'editable' : ''}`}
                        onClick={(e) => { e.stopPropagation(); if (!person.npsdPerson) startEdit(person.uuid, 'notes', person.notes); }}
                      >
                        {person.notes || '\u2014'}
                      </div>
                    )}
                  </td>

                  {/* Link */}
                  <td className="link-cell">
                    {person.npsdPerson ? (
                      <button
                        className="btn-link-action"
                        onClick={() => unlinkFromPerson(person.uuid)}
                        disabled={isReadOnly}
                        title={isReadOnly ? 'Edit mode required' : 'Unlink person'}
                      >
                        Unlink
                      </button>
                    ) : (
                      <>
                        <button
                          className="btn-link-action"
                          onClick={() => linkToPerson(person.uuid)}
                          disabled={isReadOnly}
                          title={isReadOnly ? 'Edit mode required' : 'Link person'}
                        >
                          Link
                        </button>
                        <button
                          className="btn-link-action btn-create"
                          onClick={() => createNpsdPerson(person.uuid)}
                          disabled={isReadOnly}
                          title={isReadOnly ? 'Edit mode required' : 'Create person'}
                        >
                          Create
                        </button>
                      </>
                    )}
                  </td>

                  {/* Actions */}
                  {/* TODO: Hide "Cite" button when offenderMode === 2 (Disabled).
                      Citation settings (offenderMode) are not loaded in this component.
                      Either pass offenderMode as a prop or load citation settings here. */}
                  <td>
                    <div className="actions">
                      {person.npsdPerson && canCreateCitation && (
                        <button
                          className="btn-cite"
                          onClick={() => citePerson(person.uuid)}
                          title="Create citation for this person"
                        >
                          <i className="fas fa-plus"></i> Cite
                        </button>
                      )}
                      <button
                        className="btn-delete"
                        onClick={() => openDeleteConfirm(person.uuid)}
                        disabled={isReadOnly}
                        title={isReadOnly ? 'Edit mode required' : 'Remove person'}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="mobile-cards">
        {people.length === 0 ? (
          <div className="empty-state">
            <p>No people assigned</p>
          </div>
        ) : (
          people.map((person: any) => (
            <div key={'mobile-' + person.uuid} className="card">
              <div className="card-header">
                <div className="card-header-content">
                  <div
                    className={`card-person-name ${person.npsdPerson ? 'linked' : ''}`}
                    onClick={() => person.npsdPerson && showLinkedPerson(person.uuid)}
                  >
                    {person.firstName} {person.lastName}
                  </div>
                </div>
                <button
                  className="btn-delete"
                  onClick={() => openDeleteConfirm(person.uuid)}
                  disabled={isReadOnly}
                  title={isReadOnly ? 'Edit mode required' : 'Remove person'}
                >
                  Delete
                </button>
              </div>

              <div className="card-grid">
                {FIELDS.map((fieldInfo) => (
                  <div key={fieldInfo.field} className="card-field">
                    <div className="card-field-label">{fieldInfo.label}</div>
                    {isEditing(person.uuid, fieldInfo.field) ? (
                      <div className="edit-field-mobile">
                        {fieldInfo.type === 'text' ? (
                          <input
                            className="edit-input"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            autoFocus
                            onBlur={handleSaveEdit}
                            onKeyUp={(e) => { if (e.key === 'Enter') handleSaveEdit(); }}
                          />
                        ) : fieldInfo.type === 'date' ? (
                          <div className="datetime-edit-controls-mobile">
                            <DateTimePicker
                              value={editValue}
                              type="date"
                              clearable={false}
                              appendToBody={true}
                              popupClass={datePickerPopupClass}
                              open={datePickerOpen}
                              onOpenChange={setDatePickerOpen}
                              onClose={handleDatePickerClose}
                              onConfirm={handleSaveEdit}
                              onClear={clearDate}
                              onChange={handleDateChange}
                              onKeyDown={(e) => { if (e.key === 'Tab') handleTabKey(e as any); }}
                            />
                            <button className="btn-clear-date-mobile" onClick={clearDate} title="Clear date">
                              <i className="fas fa-times-circle"></i>
                            </button>
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <div
                        className={`card-field-value ${!isReadOnly && !person.npsdPerson ? 'editable' : ''}`}
                        onClick={() => {
                          if (!person.npsdPerson) startEditMobile(person.uuid, fieldInfo.field, person[fieldInfo.field]);
                        }}
                      >
                        {person[fieldInfo.field]
                          ? (fieldInfo.type === 'date' ? formatDate(person[fieldInfo.field]) : person[fieldInfo.field])
                          : '\u2014'}
                      </div>
                    )}
                  </div>
                ))}

                {/* Notes field */}
                <div className="card-field card-field-notes">
                  <div className="card-field-label">Notes</div>
                  {isEditing(person.uuid, 'notes') ? (
                    <div className="edit-field-mobile">
                      <textarea
                        className="edit-input"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        autoFocus
                        onBlur={handleSaveEdit}
                        rows={2}
                      />
                    </div>
                  ) : (
                    <div
                      className={`card-field-value ${!isReadOnly ? 'editable' : ''}`}
                      onClick={() => startEditMobile(person.uuid, 'notes', person.notes)}
                    >
                      {person.notes || '\u2014'}
                    </div>
                  )}
                </div>
              </div>

              <div className="card-actions">
                {person.npsdPerson ? (
                  <>
                    <button
                      className="btn-link-action-mobile"
                      onClick={() => unlinkFromPerson(person.uuid)}
                      disabled={isReadOnly}
                      title={isReadOnly ? 'Edit mode required' : 'Unlink person'}
                    >
                      <i className="fas fa-unlink"></i> Unlink
                    </button>
                    {/* TODO: Also hide when offenderMode === 2 (see TODO above) */}
                    {canCreateCitation && (
                      <button
                        className="btn-link-action-mobile btn-cite-mobile"
                        onClick={() => citePerson(person.uuid)}
                        title="Create citation for this person"
                      >
                        <i className="fas fa-plus"></i> Cite
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <button
                      className="btn-link-action-mobile"
                      onClick={() => linkToPerson(person.uuid)}
                      disabled={isReadOnly}
                      title={isReadOnly ? 'Edit mode required' : 'Link person'}
                    >
                      <i className="fas fa-link"></i> Link
                    </button>
                    <button
                      className="btn-link-action-mobile btn-create-mobile"
                      onClick={() => createNpsdPerson(person.uuid)}
                      disabled={isReadOnly}
                      title={isReadOnly ? 'Edit mode required' : 'Create person'}
                    >
                      <i className="fas fa-plus"></i> Create
                    </button>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <ConfirmModal
        open={deleteConfirm.open}
        title={'Remove ' + deleteConfirm.name + '?'}
        confirmText="Remove"
        cancelText="Cancel"
        autoFocus="cancel"
        scrollLock={true}
        theme={theme}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
}

(window as any).CallCardPersonGridReact = CallCardPersonGrid;
